import type {
	ViewModeEnum,
	ComparisonOperatorDTOKeywordEnum,
	SmartWorkspaceDTO,
	SortCriterionDTO,
	ParameterizedConceptDTO,
	SmartWorkspaceDTOChart,
} from "@eu.mdotm.api/smart-workspaces";
import type { ColumnState } from "ag-grid-community";
import type { TypeOf } from "zod";
import type { chartPreferencesLatest } from "./preferences-schemas";

type WorkspaceFilter = {
	parameterizedConceptUuid: string;
	keyword: ComparisonOperatorDTOKeywordEnum;
	arguments: Array<object>;
};

type WorkspaceSorting = SortCriterionDTO;

export type ParameterizedConceptDTOWithMandatoryUuid = Omit<ParameterizedConceptDTO, "uuid"> & { uuid: string };

export type WorkspacePagination = {
	page: number;
	size: number;
	total: number;
};

export type WorkspaceChartCtx = SmartWorkspaceDTOChart & {
	preferences?: TypeOf<typeof chartPreferencesLatest>["data"];
};

export type WorkspaceColumnOrigin = "WORKSPACE" | "STANDARD";
export type WorkspaceColumnScope = "USER" | "CUSTOMER";

export type WorkspaceColumnEntry = {
	parametrizedConcept: ParameterizedConceptDTOWithMandatoryUuid;
	origin: WorkspaceColumnOrigin;
	inProjection: boolean;
	scope?: WorkspaceColumnScope;
	columnState?: ColumnState;
};

/**
 * The workspace as the adapter deserializes a document into, and as the tests describe one.
 *
 * No longer the shape anything is *stored* in: the store holds `WorkspaceState`, which draws the line where
 * it is load-bearing — what reaches the wire. This survives as the DTO-facing vocabulary on both sides of
 * `fromWorkspace` / `toWorkspace`, which is why the two have to stay lossless.
 */
export type Workspace = {
	view: ViewModeEnum;
	search: string;
	filters: WorkspaceFilter[];
	columns: ColumnState[] | null;
	charts: WorkspaceChartCtx[];
	privateParametrizedConcepts: ParameterizedConceptDTOWithMandatoryUuid[];
	sort: WorkspaceSorting | null;
	pagination: WorkspacePagination;
	selection: Array<string>;
	workspace?: SmartWorkspaceDTO;
	sharedParametrizedConcepts: ParameterizedConceptDTOWithMandatoryUuid[];
};

export const pageSizeOptions = [5, 10, 20, 30, 40, 50];
